import React, { useState, useContext } from "react";
import styled from "styled-components";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { motion, AnimatePresence } from "framer-motion";
import { wrap } from "@popmotion/popcorn";
import { Markup } from "interweave";
import { Leaf, Arrow } from "images/svgs";

const variants = {
  enter: (direction) => ({
    opacity: 0,
    x: direction > 0 ? 100 : -100,
  }),
  center: {
    opacity: 1,
    x: 0,
    zIndex: 1,
  },
  exit: (direction) => ({
    opacity: 0,
    x: direction < 0 ? 100 : -100,
    zIndex: 0,
  }),
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => Math.abs(offset) * velocity;

const SpeakerPopup = ({ startPoint, speakers }) => {
  const [[page, direction], setPage] = useState([startPoint, 0]);

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  const speakerIndex = wrap(0, speakers.length, page);

  console.log(speakerIndex, speakers, "Fuuuuuuuuuuuuuck");

  const speakerImage = getImage(speakers[speakerIndex].image);

  return (
    <Backdrop>
      <Prev
        onClick={() => paginate(-1)}
        whileTap={{ scale: 0.9 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <Arrow />
      </Prev>
      <AnimatePresence mode="wait">
        <Container
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.5 }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
        >
          <Slide>
            <SpeakerImgContainer>
              <SVGContainer>
                <Leaf />
              </SVGContainer>
              <SpeakerImg image={speakerImage} />
            </SpeakerImgContainer>
            <SpeakerDetails>
              <SpeakerName>
                <Markup content={speakers[speakerIndex].name} />
              </SpeakerName>
              <SpeakerTitle>
                <Markup content={speakers[speakerIndex].title} noHtml />
              </SpeakerTitle>
              <SpeakerSummary>
                <Markup content={speakers[speakerIndex].description} />
              </SpeakerSummary>
            </SpeakerDetails>
          </Slide>
        </Container>
      </AnimatePresence>
      <Next
        onClick={() => paginate(1)}
        whileTap={{ scale: 0.9 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.25 }}
      >
        <Arrow />
      </Next>
    </Backdrop>
  );
};

const Paginate = styled(motion.button)`
  align-items: center;
  background-color: var(--abbvie-green);
  border: none;
  cursor: pointer;
  display: flex;
  height: 3rem;
  justify-content: center;
  justify-self: end;
  left: initial;
  opacity: 0.8;
  outline: none;
  position: absolute;
  right: initial;
  top: calc(50% - 1.5rem);
  width: 3rem;
  z-index: 3;
  svg {
    transform-origin: center;
    width: 1rem;
    path {
      stroke: white;
    }
  }
`;

const Prev = styled(Paginate)`
  border-bottom-left-radius: 50%;
  border-top-left-radius: 50%;
  left: -4rem;
  opacity: 0.8;
  padding-left: 0.625rem;
  svg {
    margin-right: 0.5rem;
    transform: rotate(-180deg);
  }
  @media (min-width: 1150px) {
    left: 0;
  }
`;

const Next = styled(Paginate)`
  border-bottom-right-radius: 50%;
  border-top-right-radius: 50%;
  padding-right: 0.625rem;
  right: -4rem;
  svg {
    margin-left: 0.5rem;
    transform: rotate(360deg);
  }
  @media (min-width: 1150px) {
    right: 0;
  }
`;

const SpeakerImgContainer = styled.div`
  background: transparent;
  grid-column: 1/7;
  justify-self: center;
  margin-bottom: 1.25rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
  margin-top: -50%;
  max-width: 100%;
  position: relative;
  width: 300px;
  z-index: 1;
  overflow: visible;
  @media (min-width: 1150px) {
    align-self: start;
    grid-column: 1/5;
    height: auto;
    margin-left: -50%;
    margin-right: 50%;
    margin-top: -25px;
    max-width: 100%;
    width: 300px;
  }
`;

const SpeakerImg = styled(GatsbyImage)`
  border-radius: 50%;
  box-shadow: 0px 0.375rem 0.75rem rgba(0, 0, 0, 0.25);
  cursor: pointer;
  height: auto;
  min-height: 275px;
  object-fit: cover !important;
  object-position: center !important;
  position: relative;
  width: 100%;
  z-index: 1;
  overflow: visible;
  &:before {
    content: "";
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 100%;
    height: 100%;
    background: rgba(3, 147, 44, 0.3);
    border-radius: 50%;
    z-index: 0;
  }
  @media (min-width: 1150px) {
    min-height: 300px;
  }
`;

const SpeakerName = styled.h3`
  color: var(--abbvie-green);
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 700;
  margin-bottom: 0px;
  margin-top: 0px;
  text-align: left;
`;

const SpeakerTitle = styled.p`
  font-size: 1rem;
  font-style: normal;
  font-weight: 300;
  line-height: 1.25rem;
  margin-bottom: 1rem;
  margin-top: 0px;
  text-align: left;
  font-family: "League Spartan", sans-serif;
  br {
    display: block !important;
  }
`;

const SpeakerSummary = styled.p`
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
  a {
    color: var(--abbvie-green);
  }
`;

const SpeakerDetails = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1/7;
  font-family: "League Spartan", sans-serif;
  margin-left: 0px;

  @media (min-width: 1150px) {
    grid-column: 5/11;
    margin-left: -150px;
    padding: 2.5rem;
  }
`;

const Slide = styled.div`
  align-items: center;
  background: #f3f3f3;
  color: var(--abbvie-blue);
  height: auto;
  margin-bottom: 3rem;
  margin-top: 10rem;
  padding: 1.25rem;
  position: relative;
  width: 100%;
  grid-column: 1/7;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  overflow: visible;
  @media (min-width: 768px) {
    grid-column: 2/6;
  }
  @media (min-width: 1150px) {
    margin-left: 200px;
    margin-right: 5rem;
    margin-top: 0px;
    grid-column: 2/12;
    max-width: 900px;
    grid-template-columns: repeat(10, 1fr);
    grid-gap: 1.25rem;
  }
`;

const Container = styled(motion.div)`
  align-items: center;
  display: flex;
  justify-content: center;
  max-width: 100%;
  padding-top: 1rem;
  width: 1200px;
  overflow: visible;
  @media (min-width: 768px) {
    max-width: 400px;
  }
  @media (min-width: 1150px) {
    max-width: 100%;
    padding-top: 0;
  }
`;

const Backdrop = styled.div`
  height: auto;
  width: auto;
`;

const SVGContainer = styled.div`
  position: absolute;
  left: -13%;
  top: 4%;
  width: 62%;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2;
  svg {
    height: 100%;
    width: 100%;
  }
`;

export default SpeakerPopup;
